import {
  MetricGrouping,
  MetricIntervalPeriod,
  ServiceLevelTargetSeconds,
  VALID_SERVICE_LEVEL_TARGETS,
} from '@/types/agentEvents';
import { defaultOccupancyParams } from '@/utils/metricsParams';
import { shouldShowAgentPerformanceIntervalChart } from '@/utils/agentMetricIntervalView';
import {
  getServiceLevelTone,
  SERVICE_LEVEL_BAR_COLORS,
  SERVICE_LEVEL_TEXT_COLORS,
} from '@/utils/serviceLevelColors';
import { MetricsSection } from '../dashboard/MetricsSection';
import { api } from '@/store/api';
import { Box, Typography, LinearProgress } from '@gapartners/ui';
import { useAppSelector } from '@/store/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MetricIntervalLineChart } from '../common/MetricIntervalLineChart';

const parsePercentage = (value: string | number): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace('%', '')) || 0;
  }
  return value as number;
};

export function AgentServiceLevelSection() {
  const { user } = useAppSelector(state => state.auth);
  const [targetSeconds, setTargetSeconds] =
    useState<ServiceLevelTargetSeconds>(20);
  const { t } = useTranslation();

  return (
    <MetricsSection
      titleKey="metrics.serviceLevel.title"
      descriptionKey="metrics.serviceLevel.description"
      metricName="service-level"
      defaultParams={{
        ...defaultOccupancyParams(),
        groupings: [MetricGrouping.AGENT],
        intervalPeriod: MetricIntervalPeriod.TOTAL,
      }}
      groupingsOptions={[MetricGrouping.AGENT]}
      extraParams={{ targetSeconds }}
      useQuery={api.useGetAgentServiceLevelMetricsQuery}
      extraControls={() => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('components.dashboard.targetSeconds')}
          </Typography>
          <Box
            component="select"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1.5,
              py: 1,
              minWidth: 120,
            }}
            value={targetSeconds}
            onChange={e =>
              setTargetSeconds(
                Number(
                  (e.target as HTMLSelectElement).value
                ) as ServiceLevelTargetSeconds
              )
            }
          >
            {VALID_SERVICE_LEVEL_TARGETS.map(s => (
              <Box component="option" key={s} value={s}>
                {s} s
              </Box>
            ))}
          </Box>
        </Box>
      )}
      renderData={({ data, params }) => {
        const myMetrics = data.filter(
          item => item.dimensionLabels?.AGENT === user?.email
        );

        const showIntervalChart = shouldShowAgentPerformanceIntervalChart(
          params.intervalPeriod,
          myMetrics
        );

        if (showIntervalChart) {
          return (
            <Box sx={{ width: '100%', minHeight: 300, mt: 1 }}>
              <MetricIntervalLineChart
                data={myMetrics}
                valueToNumber={parsePercentage}
                valueFormatter={v => {
                  const n = parsePercentage(v);
                  return `${n.toFixed(1)}%`;
                }}
                height={300}
              />
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {myMetrics.map((item, index) => {
              const percentage = parsePercentage(item.value);
              const tone = getServiceLevelTone(percentage);
              return (
                <Box key={item.id || index}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {item.dimensionLabels?.AGENT || 'Agent'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: SERVICE_LEVEL_TEXT_COLORS[tone],
                      }}
                    >
                      {percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(percentage, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#E5E7EB',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: SERVICE_LEVEL_BAR_COLORS[tone],
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        );
      }}
    />
  );
}
